import React, { useState, useEffect, useCallback } from 'react';
import { getAbsences, getConflitsAbsence, createAbsence, deleteAbsence, getTechniciens } from '../services/api';

/**
 * Absences techniciens — congés, arrêts et journées bloquées.
 *
 * Deux rôles. Pour l'exploitation, savoir qui est là. Pour le moteur
 * d'ouverture des créneaux E-RDV, ne rien promettre sur une journée où le
 * technicien n'est pas disponible.
 *
 * Les interventions déjà posées sur la période sont montrées avant validation :
 * déclarer un congé ne peut pas annuler un rendez-vous que le client a déjà
 * obtenu, il faut le replanifier avec Covage.
 */

const PORTEE_LIBELLE = { journee: 'Journée entière', matin: 'Matin', apresmidi: 'Après-midi' };

const aujourdhui = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });

function fmt(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
}

function nbJours(debut, fin) {
  if (!debut || !fin) return 0;
  return Math.round((new Date(fin + 'T12:00:00') - new Date(debut + 'T12:00:00')) / 86400000) + 1;
}

export default function Absences() {
  const [items, setItems]       = useState([]);
  const [motifs, setMotifs]     = useState(['Congé']);
  const [techs, setTechs]       = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]     = useState(null);
  const [erreurs, setErreurs]   = useState([]);
  const [passees, setPassees]   = useState(false);

  const [form, setForm] = useState({
    technicien: '', debut: aujourdhui(), fin: aujourdhui(),
    portee: 'journee', motif: 'Congé', commentaire: '',
  });
  const [conflits, setConflits] = useState(null);
  const [verif, setVerif]       = useState(false);
  const [envoi, setEnvoi]       = useState(false);

  const recharger = useCallback(() => {
    const params = passees ? {} : { depuis: aujourdhui() };
    return getAbsences(params)
      .then(d => { setItems(d.items || []); if (d.motifs) setMotifs(d.motifs); })
      .catch(() => setErreur('Chargement impossible'));
  }, [passees]);

  useEffect(() => {
    setChargement(true);
    Promise.all([
      recharger(),
      getTechniciens().then(d => {
        const l = (d.techniciens || []).filter(t => t.zoho_nom && t.actif !== false);
        setTechs(l);
        if (l.length) setForm(f => f.technicien ? f : { ...f, technicien: l[0].zoho_nom });
      }).catch(() => {}),
    ]).finally(() => setChargement(false));
  }, [recharger]);

  // Une demi-journée ne porte que sur une date : on aligne la fin sur le début.
  const set = (k, v) => setForm(f => {
    const n = { ...f, [k]: v };
    if (k === 'portee' && v !== 'journee') n.fin = n.debut;
    if (k === 'debut') {
      if (n.portee !== 'journee' || n.fin < v) n.fin = v;
    }
    return n;
  });

  useEffect(() => { setConflits(null); }, [form.technicien, form.debut, form.fin]);

  const verifier = async () => {
    setVerif(true); setErreurs([]);
    try {
      const d = await getConflitsAbsence({ technicien: form.technicien, debut: form.debut, fin: form.fin });
      setConflits(d);
    } catch (e) {
      setErreurs([e.response?.data?.error || 'Vérification impossible']);
    } finally { setVerif(false); }
  };

  const enregistrer = async () => {
    setEnvoi(true); setErreurs([]);
    try {
      const r = await createAbsence(form);
      setConflits({ items: r.conflits || [], tronque: r.conflits_tronques, apres: true });
      setForm(f => ({ ...f, commentaire: '' }));
      await recharger();
    } catch (e) {
      const d = e.response?.data;
      setErreurs(d?.erreurs || [d?.error || 'Enregistrement impossible']);
    } finally { setEnvoi(false); }
  };

  const retirer = async (it) => {
    if (!window.confirm(`Supprimer l'absence de ${it.technicien} du ${it.debut} au ${it.fin} ?`)) return;
    try { await deleteAbsence(it.id); await recharger(); }
    catch { setErreurs(['Suppression impossible']); }
  };

  const nomDe = z => {
    const t = techs.find(x => x.zoho_nom === z);
    return t ? [t.prenom, t.nom].filter(Boolean).join(' ') : '';
  };

  const champ = { padding: '7px 10px', fontSize: 13, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' };

  if (chargement) return (
    <>
      <div className="topbar"><div className="topbar-title">Absences techniciens</div></div>
      <div className="page"><div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>Chargement…</div></div>
    </>
  );

  const duree = nbJours(form.debut, form.fin);

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Absences techniciens</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
          <input type="checkbox" checked={passees} onChange={e => setPassees(e.target.checked)} />
          Afficher aussi les absences passées
        </label>
      </div>

      <div className="page">
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18, lineHeight: 1.6, maxWidth: 820 }}>
          Une journée déclarée ici retire le technicien de la capacité : le moteur n'ouvrira aucun
          créneau E-RDV sur cette période, et aucune date ne sera proposée. Si le département n'a
          qu'un technicien, il se ferme ; s'ils sont deux, la capacité tombe de moitié.
        </div>

        {/* ── Saisie ── */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Technicien</div>
              <select value={form.technicien} onChange={e => set('technicien', e.target.value)} style={{ ...champ, minWidth: 220 }}>
                {techs.map(t => (
                  <option key={t.zoho_nom} value={t.zoho_nom}>
                    {t.zoho_nom} — {[t.prenom, t.nom].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Du</div>
              <input type="date" value={form.debut} onChange={e => set('debut', e.target.value)} style={champ} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Au</div>
              <input type="date" value={form.fin} min={form.debut} disabled={form.portee !== 'journee'}
                onChange={e => set('fin', e.target.value)}
                style={{ ...champ, opacity: form.portee !== 'journee' ? .5 : 1 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Portée</div>
              <select value={form.portee} onChange={e => set('portee', e.target.value)} style={champ}>
                {Object.entries(PORTEE_LIBELLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Motif</div>
              <select value={form.motif} onChange={e => set('motif', e.target.value)} style={champ}>
                {motifs.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Commentaire</div>
              <input value={form.commentaire} onChange={e => set('commentaire', e.target.value)}
                placeholder="facultatif" style={{ ...champ, width: '100%' }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>
              {form.portee === 'journee'
                ? `${duree} jour${duree > 1 ? 's' : ''} calendaire${duree > 1 ? 's' : ''}`
                : PORTEE_LIBELLE[form.portee] + ' du ' + fmt(form.debut)}
            </span>
            <button className="btn btn-sm" onClick={verifier} disabled={verif || !form.technicien}>
              {verif ? 'Vérification…' : 'Vérifier les interventions posées'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={enregistrer} disabled={envoi || !form.technicien}>
              {envoi ? 'Enregistrement…' : 'Déclarer l\'absence'}
            </button>
          </div>

          {erreurs.length > 0 && (
            <div style={{ marginTop: 12, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.35)', borderRadius: 6, padding: '10px 14px' }}>
              {erreurs.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#ff6b6b' }}>• {e}</div>)}
            </div>
          )}

          {conflits && (
            <div style={{
              marginTop: 12, borderRadius: 6, padding: '10px 14px',
              background: conflits.items.length ? 'rgba(219,139,0,.12)' : 'rgba(46,160,67,.12)',
              border: '1px solid ' + (conflits.items.length ? 'rgba(219,139,0,.4)' : 'rgba(46,160,67,.35)'),
            }}>
              {conflits.tronque ? (
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  Période de plus de soixante jours : les interventions posées n'ont pas été vérifiées.
                </div>
              ) : conflits.items.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--green2)', fontWeight: 600 }}>
                  ✓ Aucune intervention posée sur cette période
                  {conflits.apres ? ' — absence enregistrée.' : '.'}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#d98a00', marginBottom: 8 }}>
                    {conflits.items.length} intervention{conflits.items.length > 1 ? 's' : ''} déjà posée
                    {conflits.items.length > 1 ? 's' : ''} sur cette période
                    {conflits.items.filter(c => c.erdv).length > 0 && (
                      <> — dont <strong>{conflits.items.filter(c => c.erdv).length} en E-RDV, à replanifier avec Covage</strong></>
                    )}
                  </div>
                  <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                    <tbody>
                      {conflits.items.map((c, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '3px 8px', whiteSpace: 'nowrap' }}>{fmt(c.date)}</td>
                          <td style={{ padding: '3px 8px', fontWeight: 700 }}>{c.heure || '--:--'}</td>
                          <td style={{ padding: '3px 8px' }}>{c.ref_commande || '—'}</td>
                          <td style={{ padding: '3px 8px' }}>
                            {c.erdv && <span style={{ color: '#d98a00', fontWeight: 700 }}>🔒 E-RDV</span>}
                          </td>
                          <td style={{ padding: '3px 8px' }}>{c.typo}</td>
                          <td style={{ padding: '3px 8px', color: 'var(--text3)' }}>{c.ville}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Liste ── */}
        {items.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: 24, textAlign: 'center' }}>
            Aucune absence {passees ? 'enregistrée' : 'à venir'}.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <thead>
              <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>Technicien</th>
                <th style={{ padding: '8px 12px' }}>Période</th>
                <th style={{ padding: '8px 12px' }}>Portée</th>
                <th style={{ padding: '8px 12px' }}>Motif</th>
                <th style={{ padding: '8px 12px' }}>Commentaire</th>
                <th style={{ padding: '8px 12px' }}>Saisi par</th>
                <th style={{ padding: '8px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const passee = it.fin < aujourdhui();
                const n = nbJours(it.debut, it.fin);
                return (
                  <tr key={it.id} style={{ borderTop: '1px solid var(--border)', opacity: passee ? .5 : 1 }}>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontWeight: 700 }}>{it.technicien}</div>
                      {nomDe(it.technicien) && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{nomDe(it.technicien)}</div>}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      {it.debut === it.fin ? fmt(it.debut) : `${fmt(it.debut)} → ${fmt(it.fin)}`}
                      {n > 1 && <span style={{ color: 'var(--text3)' }}> ({n} j)</span>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{PORTEE_LIBELLE[it.portee] || it.portee}</td>
                    <td style={{ padding: '8px 12px' }}>{it.motif}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)' }}>{it.commentaire || '—'}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)', fontSize: 10 }}>{it.cree_par || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <button className="btn btn-sm" onClick={() => retirer(it)} style={{ color: '#ff6b6b' }}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
